/**
 * EventBus: lightweight typed event bus for UI / scene events.
 *
 * Per the architecture doc, UI and business events flow through a single bus
 * instead of being wired into PlayCanvas scripts.
 */

import type { SceneEventAction } from '../scene/SceneObjectTypes';

export type SceneEventType =
    | 'HOTSPOT_ENTER'
    | 'HOTSPOT_EXIT'
    | 'HOTSPOT_CLICK'
    | 'ZONE_ENTER'
    | 'ZONE_EXIT'
    | 'PORTAL_ENTER'
    | 'PORTAL_EXIT'
    | 'LOAD_ERROR'
    | 'LOAD_SUCCESS'
    | 'MODE_CHANGE'
    | 'SCENE_LOAD_START'
    | 'SCENE_READY'
    | 'SCENE_UNLOADED'
    | 'OBJECT_TRIGGER'
    | 'OBJECT_SELECT'
    | 'SCENE_VISIBILITY';

export interface SceneEvent {
    type: SceneEventType;
    id?: string;
    name?: string;
    message?: string;
    mode?: 'fly' | 'walk' | 'third';
    viewMode?: 'first' | 'third';
    moveMode?: 'fly' | 'walk';
    /** Portal target scene id (PORTAL_ENTER). */
    targetScene?: string;
    targetRoomId?: string;
    /** Portal target spawn (PORTAL_ENTER). */
    targetSpawn?: [number, number, number];
    targetAngles?: [number, number];
    /** Load progress 0..1 (SCENE_LOAD_START). */
    progress?: number;
    /** Id of the SceneObject that triggered (OBJECT_TRIGGER / OBJECT_SELECT). */
    objectId?: string;
    /** A single action to dispatch (OBJECT_TRIGGER, convenience for one-shot). */
    action?: SceneEventAction;
    /** All actions to dispatch (OBJECT_TRIGGER). */
    actions?: SceneEventAction[];
    /** Editor transform-mode hint (OBJECT_SELECT). */
    transform?: 'select' | 'move' | 'rotate' | 'scale' | 'place';
    /** Whether scene content should be visible (SCENE_VISIBILITY). */
    visible?: boolean;
}

type Handler = (event: SceneEvent) => void;

class EventBusClass {
    private _handlers = new Map<SceneEventType, Handler[]>();

    on(type: SceneEventType, handler: Handler): void {
        const list = this._handlers.get(type) ?? [];
        list.push(handler);
        this._handlers.set(type, list);
    }

    off(type: SceneEventType, handler: Handler): void {
        const list = this._handlers.get(type);
        if (!list) return;
        const idx = list.indexOf(handler);
        if (idx >= 0) list.splice(idx, 1);
    }

    emit(event: SceneEvent): void {
        const list = this._handlers.get(event.type);
        if (!list) return;
        for (const handler of list) handler(event);
    }
}

export const eventBus = new EventBusClass();
export { EventBusClass as EventBus };
